import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Sync as SyncIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Image as ImageIcon,
  ImageNotSupported as NoImageIcon,
  Search as SearchIcon,
  LocalBar as LiquorIcon,
  Download as DownloadIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import authInstance from '../../services/httpClient';
import UberCatalogueReview, {
  type CatalogueReview, type CatalogueDecisions, emptyDecisions, countDecisions,
} from './UberCatalogueReview';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FilteredProductDetail {
  sku: string;
  name: string;
  reason: string;
  category?: string;
}

interface FilterBreakdown {
  noCategory: FilteredProductDetail[];
  inactiveCategory: FilteredProductDetail[];
  excludedCategory: FilteredProductDetail[];
  unmappedCategory: FilteredProductDetail[];
  inactiveMapping: FilteredProductDetail[];
  zeroPriceAfterMarkup: FilteredProductDetail[];
  itemIdCollision: FilteredProductDetail[];
}

interface MenuBuildStats {
  totalProducts: number;
  includedProducts: number;
  filteredOutProducts: number;
  categoriesMatched: number;
  categoriesUnmatched: number;
  itemsWithImages: number;
  itemsWithoutImages: number;
  imagesUploaded: number;
  imagesReused: number;
  menuCount: number;
  filterBreakdown?: FilterBreakdown;
}

interface SyncPreviewData {
  menu: any;
  stats: MenuBuildStats;
  warnings: string[];
  /** 'catalogue' once the Uber catalogue is set up (then review is there too) */
  mode?: 'catalogue';
  review?: CatalogueReview;
  /** catalogue mode: items on Uber now that this push takes off */
  removed?: Array<{ id: string; sku: string; name: string; reason: string }>;
  /** catalogue mode: items changed on Uber directly that this push puts ours back over */
  uberChanged?: Array<{ sku: string; name: string; fields: Array<{ field: string; ours: string; uber: string }> }>;
}

interface SyncPushResult {
  backupKey: string;
  itemCount: number;
  categoryCount: number;
  menuCount: number;
}

interface Props {
  onBack: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GREEN = '#2e7d32';
const GREEN_DARK = '#1b5e20';
const GREEN_LIGHT = '#e8f5e9';
const STEPS = ['Build Preview', 'Review Menu', 'Push to Uber'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(cents / 100);

function extractTitle(titleObj: any): string {
  if (!titleObj) return '';
  if (typeof titleObj === 'string') return titleObj;
  if (titleObj?.translations?.en) return titleObj.translations.en;
  if (typeof titleObj === 'object') return (Object.values(titleObj)[0] as string) || '';
  return '';
}

// ---------------------------------------------------------------------------
// Custom Stepper
// ---------------------------------------------------------------------------

const WizardStepper: React.FC<{ activeStep: number }> = ({ activeStep }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      mb: 4,
      px: 2,
    }}
  >
    {STEPS.map((label, idx) => {
      const isCompleted = idx < activeStep;
      const isActive = idx === activeStep;
      const isFuture = idx > activeStep;

      return (
        <React.Fragment key={label}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 72 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isCompleted ? '#43a047' : isActive ? GREEN : '#e0e0e0',
                color: '#fff',
                fontWeight: 700,
                fontSize: 15,
                transition: 'background-color 0.3s ease',
                boxShadow: isActive ? `0 0 0 4px ${GREEN_LIGHT}` : 'none',
              }}
            >
              {isCompleted ? <CheckIcon sx={{ fontSize: 22 }} /> : idx + 1}
            </Box>
            <Typography
              variant="caption"
              sx={{
                mt: 0.75,
                fontWeight: isActive ? 700 : 500,
                color: isActive ? GREEN : isFuture ? 'text.disabled' : 'text.secondary',
                transition: 'color 0.3s ease',
              }}
            >
              {label}
            </Typography>
          </Box>
          {idx < STEPS.length - 1 && (
            <Box
              sx={{
                flex: 1,
                height: 3,
                mx: 1,
                mt: -2,
                borderRadius: 2,
                bgcolor: idx < activeStep ? '#43a047' : '#e0e0e0',
                transition: 'background-color 0.3s ease',
              }}
            />
          )}
        </React.Fragment>
      );
    })}
  </Box>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const SyncAppToUber: React.FC<Props> = ({ onBack }) => {
  // Wizard state
  const [step, setStep] = useState(0);

  // Step 1 — Build
  const [building, setBuilding] = useState(false);

  // Step 2 — Preview
  const [previewData, setPreviewData] = useState<SyncPreviewData | null>(null);
  const [searchFilter, setSearchFilter] = useState('');

  // Step 3 — Push
  const [pushing, setPushing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pushResult, setPushResult] = useState<SyncPushResult | null>(null);

  // Image preview
  const [imagePreview, setImagePreview] = useState<{ url: string; name: string; sku: string } | null>(null);

  // Price override editor — REMOVED: price editing now lives on Product Management page

  // Uber catalogue review: what was decided for new / mismatching products (sent with the push)
  const [decisions, setDecisions] = useState<CatalogueDecisions>(emptyDecisions());

  // Shared
  const [error, setError] = useState<string | null>(null);

  // Uber markup divisor (fetched from system settings)
  const [markupDivisor, setMarkupDivisor] = useState(0.8275);

  useEffect(() => {
    authInstance.get('/admin/tools/uber-markup')
      .then((res) => { if (res.data.success) setMarkupDivisor(res.data.data.divisor); })
      .catch(() => {}); // fallback to default
  }, []);

  // ---------------------------------------------------------------------------
  // Step 1 — Build Preview
  // ---------------------------------------------------------------------------

  const handleBuildPreview = useCallback(async () => {
    setBuilding(true);
    setError(null);
    setPreviewData(null);
    setPushResult(null);
    setDecisions(emptyDecisions());

    try {
      const response = await authInstance.post(
        '/admin/tools/uber-menu/sync-preview',
        // The store is always ours (Uber Eats is Deeliver-only); what goes is decided by the
        // Uber Menu Mappings, so there is no hand-kept exclude list any more.
        { menuMode: 'tag-based' },
        { timeout: 120000 },
      );

      if (response.data.success) {
        setPreviewData(response.data.data);
        setStep(1);
      } else {
        setError(response.data.error || 'Build failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to build menu preview');
    } finally {
      setBuilding(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Step 3 — Push to Uber
  // ---------------------------------------------------------------------------

  const handlePush = async (force = false, expectRemoved = previewData?.removed?.length ?? 0) => {
    if (!previewData) return;
    setConfirmOpen(false);
    setPushing(true);
    setError(null);

    try {
      const response = await authInstance.post(
        '/admin/tools/uber-menu/sync-push',
        {
          menuMode: 'tag-based',
          ...(force ? { force: true } : {}),
          ...(previewData.mode === 'catalogue' ? { decisions, expectRemoved } : {}),
        },
        { timeout: 180000 },
      );

      if (response.data.success) {
        setPushResult(response.data.data);
        setStep(2);
      } else {
        setError(response.data.error || 'Push failed');
      }
    } catch (err: any) {
      // The backend refuses a menu that drops more than a fifth of the live items until confirmed.
      if (!force && err.response?.status === 409 && err.response?.data?.code === 'MENU_SHRINK') {
        setPushing(false);
        if (window.confirm(`${err.response.data.error}

Push this smaller menu anyway?`)) {
          await handlePush(true, expectRemoved);
          return;
        }
        setError(err.response.data.error);
        return;
      }
      // More would leave Uber than the preview showed (something changed since): name them and ask.
      if (err.response?.status === 409 && err.response?.data?.code === 'MENU_REMOVES') {
        setPushing(false);
        const removed: Array<{ sku: string; name: string; reason: string }> = err.response.data.removed || [];
        const list = removed.slice(0, 15).map((x) => `- ${x.name} (${x.sku}): ${x.reason}`).join('\n');
        if (window.confirm(`${err.response.data.error}

${list}${removed.length > 15 ? `\n...and ${removed.length - 15} more` : ''}

Take them off Uber and push?`)) {
          await handlePush(force, removed.length);
          return;
        }
        setError(err.response.data.error);
        return;
      }
      setError(err.response?.data?.error || err.message || 'Failed to push menu to Uber');
    } finally {
      setPushing(false);
    }
  };

  // Price override editing removed — now managed on Product Management page

  // ---------------------------------------------------------------------------
  // Render — Step 1: Configure & Build
  // ---------------------------------------------------------------------------

  const renderStep1 = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        Once the Uber catalogue is set up (Uber Menu page), the menu is built from it: every item with its own Uber name,
        description, image, aisle and price. The build also lists <strong>new Lightspeed products</strong> and products
        <strong> mismatching Lightspeed</strong> for you to settle before pushing. Aisle names and order come from
        <strong> Uber Tools → Uber Menu Mappings</strong>.
      </Alert>

      <Card variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: GREEN }}>
            <SyncIcon sx={{ fontSize: 18, mr: 0.75, verticalAlign: 'text-bottom' }} />
            Build the Uber Menu
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Builds the full Uber Eats menu for you to review. Nothing changes on Uber until you push.
          </Typography>

          <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 2, p: 2, mb: 2.5, color: '#333' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: '#333' }}>
              What this does:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2, '& li': { mb: 0.5, fontSize: '0.8rem', color: '#555' } }}>
              <li>Puts every item from the Uber Menu page on Uber, except items switched off there (On menu) and items whose aisle is switched off in Uber Menu Mappings</li>
              <li>Each item shows the name, description, image and price saved for it on the Uber Menu page. Stock and sold-out / on-hold state are not changed: each item keeps what it has on Uber now</li>
              <li>New Lightspeed products you add get <code>Tier 1 ÷ {markupDivisor}</code> to the nearest 50c</li>
              <li>Keeps each item's Uber id, so sales history, ranking and promotions carry over</li>
              <li>On push: backs up the live menu first, then replaces it</li>
            </Box>
          </Box>

          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleBuildPreview}
            disabled={building}
            startIcon={building ? <CircularProgress size={22} color="inherit" /> : <SyncIcon />}
            sx={{
              bgcolor: GREEN,
              '&:hover': { bgcolor: GREEN_DARK },
              py: 1.75,
              fontSize: '1.05rem',
              fontWeight: 700,
              borderRadius: 3,
              textTransform: 'none',
            }}
          >
            {building ? 'Building Menu Preview...' : 'Build Preview'}
          </Button>

          {building && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                sx={{
                  borderRadius: 2,
                  '& .MuiLinearProgress-bar': { bgcolor: GREEN },
                  bgcolor: GREEN_LIGHT,
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                Querying products, loading images, building menu structure...
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Render — Step 2: Preview
  // ---------------------------------------------------------------------------

  const renderStep2 = () => {
    if (!previewData) return null;

    const { menu, stats, warnings } = previewData;
    const menus: any[] = menu.menus || [];
    const categories: any[] = menu.categories || [];
    const items: any[] = menu.items || [];

    // Build item lookup for fast access
    const itemMap = new Map<string, any>();
    for (const item of items) {
      itemMap.set(item.id, item);
    }

    // Build category → items lookup
    const catItemsMap = new Map<string, any[]>();
    for (const cat of categories) {
      const catItems: any[] = [];
      for (const entity of (cat.entities || [])) {
        const item = itemMap.get(entity.id);
        if (item) catItems.push(item);
      }
      catItemsMap.set(cat.id, catItems);
    }

    // Filter items by search
    const filterLower = searchFilter.toLowerCase();
    const matchesFilter = (item: any): boolean => {
      if (!filterLower) return true;
      const name = extractTitle(item.title).toLowerCase();
      const sku = (item.external_data || '').toLowerCase();
      return name.includes(filterLower) || sku.includes(filterLower);
    };

    const dc = countDecisions(decisions);
    return (
      <Box>
        {previewData.mode === 'catalogue' && previewData.review && (
          <>
            <UberCatalogueReview review={previewData.review} decisions={decisions} onChange={setDecisions} />
            {(dc.added + dc.declined + dc.fields) > 0 && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                The preview below is the menu as it stands; what you decided above
                ({dc.added} to add, {dc.declined} declined, {dc.fields} change{dc.fields === 1 ? '' : 's'}) goes in when you push.
              </Alert>
            )}
          </>
        )}

        {previewData.mode === 'catalogue' && (previewData.uberChanged?.length ?? 0) > 0 && (
          <Accordion sx={{ mb: 2, borderRadius: '8px !important', border: '1px solid #ffcc80', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <WarningIcon sx={{ color: '#e65100', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e65100' }}>
                  Changed on Uber: {previewData.uberChanged!.length} item{previewData.uberChanged!.length === 1 ? '' : 's'} differ on Uber; this push puts ours back
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                To keep what Uber shows instead, use Take Uber's on the Uber Menu page (filter: Changed on Uber), then build the preview again.
              </Typography>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td, & th': { px: 1.5, py: 0.5, fontSize: '0.78rem', borderBottom: '1px solid #f0f0f0', verticalAlign: 'top' }, '& th': { fontWeight: 700, color: 'text.secondary', textAlign: 'left', bgcolor: '#fafafa' } }}>
                <thead><tr><th style={{ width: 90 }}>SKU</th><th>Name</th><th>On Uber now → ours</th></tr></thead>
                <tbody>
                  {previewData.uberChanged!.slice(0, 100).map((x) => (
                    <tr key={x.sku}>
                      <td>{x.sku}</td>
                      <td>{x.name}</td>
                      <td>{x.fields.map((f) => (f.field === 'image' ? 'image' : `${f.field}: ${f.uber || '(none)'} → ${f.ours || '(none)'}`)).join(' · ')}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {previewData.mode === 'catalogue' && (previewData.removed?.length ?? 0) > 0 && (
          <Accordion sx={{ mb: 2, borderRadius: '8px !important', border: '1px solid #ffcc80', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <WarningIcon sx={{ color: '#e65100', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e65100' }}>
                  Leaving Uber: {previewData.removed!.length} item{previewData.removed!.length === 1 ? '' : 's'} on Uber now come off with this push
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td, & th': { px: 1.5, py: 0.5, fontSize: '0.78rem', borderBottom: '1px solid #f0f0f0' }, '& th': { fontWeight: 700, color: 'text.secondary', textAlign: 'left', bgcolor: '#fafafa' } }}>
                <thead><tr><th style={{ width: 90 }}>SKU</th><th>Name</th><th>Why</th></tr></thead>
                <tbody>
                  {previewData.removed!.slice(0, 100).map((x) => (
                    <tr key={x.id}><td>{x.sku || '—'}</td><td>{x.name}</td><td><Typography variant="caption" color="text.secondary">{x.reason}</Typography></td></tr>
                  ))}
                  {previewData.removed!.length > 100 && (
                    <tr><td colSpan={3}><Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>...and {previewData.removed!.length - 100} more</Typography></td></tr>
                  )}
                </tbody>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Summary cards */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          {[
            { label: 'Total Items', value: stats.includedProducts, color: GREEN },
            { label: 'Categories', value: categories.length, color: '#1565c0' },
            { label: 'Menus', value: stats.menuCount, color: '#6a1b9a' },
            { label: 'With Images', value: stats.itemsWithImages, color: '#43a047' },
            { label: 'No Image', value: stats.itemsWithoutImages, color: stats.itemsWithoutImages > 0 ? '#e65100' : '#9e9e9e' },
            { label: 'Filtered Out', value: stats.filteredOutProducts, color: '#757575' },
          ].map((card) => (
            <Card
              key={card.label}
              variant="outlined"
              sx={{
                flex: '1 1 120px',
                minWidth: 120,
                borderRadius: 2,
                borderColor: card.color + '40',
              }}
            >
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: card.color }}>
                  {card.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {card.label}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Image stats + download */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            icon={<ImageIcon sx={{ fontSize: '16px !important' }} />}
            label={`${stats.imagesReused} reused from S3`}
            size="small"
            variant="outlined"
            sx={{ borderColor: '#43a047', color: '#2e7d32' }}
          />
          <Chip
            icon={<ImageIcon sx={{ fontSize: '16px !important' }} />}
            label={`${stats.imagesUploaded} newly uploaded`}
            size="small"
            variant="outlined"
            sx={{ borderColor: '#1565c0', color: '#0d47a1' }}
          />
          <Chip
            label={`${stats.categoriesMatched} categories`}
            size="small"
            variant="outlined"
          />
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => {
              // Strip internal preview-only fields before download
              const cleanMenu = {
                ...menu,
                items: (menu.items || []).map(({ _productId, _stockQty, _isCustomPrice, _customPriceCents, ...rest }: any) => rest),
              };
              const blob = new Blob([JSON.stringify(cleanMenu, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `uber-menu-preview-${new Date().toISOString().slice(0, 10)}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            sx={{ borderColor: '#757575', color: '#555', textTransform: 'none', fontWeight: 600 }}
          >
            Download JSON
          </Button>
        </Box>

        {/* Filter breakdown — explains why products were excluded */}
        {stats.filterBreakdown && stats.filteredOutProducts > 0 && (() => {
          const fb = stats.filterBreakdown!;
          const sections: { label: string; color: string; items: FilteredProductDetail[] }[] = [
            { label: 'No Category Assigned', color: '#d32f2f', items: fb.noCategory },
            { label: 'Inactive Category', color: '#e65100', items: fb.inactiveCategory },
            { label: 'Excluded Category', color: '#f57c00', items: fb.excludedCategory },
            { label: 'Unmapped Category (no Uber mapping)', color: '#7b1fa2', items: fb.unmappedCategory },
            { label: 'Inactive Uber Mapping', color: '#5d4037', items: fb.inactiveMapping },
            { label: '$0 Uber Price After Markup', color: '#c62828', items: fb.zeroPriceAfterMarkup },
            { label: 'Item ID Collision', color: '#37474f', items: fb.itemIdCollision },
          ].filter(s => s.items.length > 0);

          return (
            <Accordion
              sx={{ mb: 2, borderRadius: '8px !important', border: '1px solid #ef9a9a', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <FilterListIcon sx={{ color: '#d32f2f', fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                    {stats.filteredOutProducts} Products Filtered Out — Click to see why
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {sections.map((section) => (
                  <Accordion
                    key={section.label}
                    sx={{ mb: 0.5, boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: '6px !important', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: section.color }}>
                          {section.label}
                        </Typography>
                        <Chip label={section.items.length} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: section.color + '15', color: section.color }} />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Box
                        component="table"
                        sx={{ width: '100%', borderCollapse: 'collapse', '& td, & th': { px: 1.5, py: 0.5, fontSize: '0.78rem', borderBottom: '1px solid #f0f0f0' }, '& th': { fontWeight: 700, color: 'text.secondary', textAlign: 'left', bgcolor: '#fafafa' } }}
                      >
                        <thead>
                          <tr>
                            <th style={{ width: 90 }}>SKU</th>
                            <th>Name</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {section.items.slice(0, 50).map((item, i) => (
                            <tr key={`${item.sku}-${i}`}>
                              <td><Chip label={item.sku} size="small" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 22 }} /></td>
                              <td>{item.name}</td>
                              <td><Typography variant="caption" color="text.secondary">{item.reason}</Typography></td>
                            </tr>
                          ))}
                          {section.items.length > 50 && (
                            <tr><td colSpan={3}><Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>...and {section.items.length - 50} more</Typography></td></tr>
                          )}
                        </tbody>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })()}

        {/* Warnings panel */}
        {warnings.length > 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 3, borderRadius: 2, maxHeight: 200, overflow: 'auto' }}
            icon={<WarningIcon />}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {warnings.length} warning{warnings.length !== 1 ? 's' : ''}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2, '& li': { fontSize: '0.8rem', mb: 0.25 } }}>
              {warnings.slice(0, 20).map((w, i) => (
                <li key={i}>{w}</li>
              ))}
              {warnings.length > 20 && (
                <li><em>...and {warnings.length - 20} more</em></li>
              )}
            </Box>
          </Alert>
        )}

        {/* Search / filter */}
        <TextField
          placeholder="Filter items by name or SKU..."
          size="small"
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Menu tree view */}
        {menus.map((menuDef: any) => {
          const menuTitle = extractTitle(menuDef.title) || menuDef.id;
          const menuCatIds: string[] = menuDef.category_ids || [];
          const menuCats = categories.filter((c: any) => menuCatIds.includes(c.id));
          const menuItemCount = menuCats.reduce(
            (sum: number, c: any) => sum + (catItemsMap.get(c.id) || []).filter(matchesFilter).length,
            0,
          );
          const hasLiquor = menuCats.some((c: any) => {
            const catItems = catItemsMap.get(c.id) || [];
            return catItems.some((item: any) => item.dish_info?.classifications?.alcoholic_items === 1);
          });

          return (
            <Accordion
              // collapsed to start; open while a search filter is set, so matches are visible
              key={`${menuDef.id}${filterLower ? ':f' : ''}`}
              defaultExpanded={!!filterLower}
              sx={{ mb: 1, borderRadius: '8px !important', border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  {hasLiquor && (
                    <Tooltip title="Contains liquor categories (early cutoff applied)">
                      <LiquorIcon sx={{ color: '#e65100', fontSize: 20 }} />
                    </Tooltip>
                  )}
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {menuTitle}
                  </Typography>
                  <Chip
                    label={`${menuCats.length} categories`}
                    size="small"
                    sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '0.75rem' }}
                  />
                  <Chip
                    label={`${menuItemCount} items`}
                    size="small"
                    sx={{ bgcolor: GREEN_LIGHT, color: GREEN, fontWeight: 600, fontSize: '0.75rem' }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {menuCats.map((cat: any) => {
                  const catTitle = extractTitle(cat.title) || cat.id;
                  const catItems = (catItemsMap.get(cat.id) || []).filter(matchesFilter);

                  if (catItems.length === 0 && filterLower) return null;

                  return (
                    <Accordion
                      key={`${cat.id}${filterLower ? ':f' : ''}`}
                      defaultExpanded={!!filterLower}
                      sx={{
                        mb: 0.5,
                        boxShadow: 'none',
                        border: '1px solid #e0e0e0',
                        borderRadius: '6px !important',
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {catTitle}
                          </Typography>
                          <Chip
                            label={catItems.length}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <Box
                          component="table"
                          sx={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            '& td, & th': {
                              px: 1.5,
                              py: 0.75,
                              fontSize: '0.8rem',
                              borderBottom: '1px solid #f0f0f0',
                            },
                            '& th': {
                              fontWeight: 700,
                              color: 'text.secondary',
                              textAlign: 'left',
                              bgcolor: '#fafafa',
                            },
                            '& tr[style*="pointer"]:hover': {
                              bgcolor: '#f5f5f5',
                            },
                          }}
                        >
                          <thead>
                            <tr>
                              <th style={{ width: 28 }}></th>
                              <th>Name</th>
                              <th style={{ width: 90 }}>SKU</th>
                              <th style={{ width: 55 }}>Stock</th>
                              <th style={{ width: 90 }}>App Price</th>
                              <th style={{ width: 120 }}>Uber Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {catItems.map((item: any) => {
                              const name = extractTitle(item.title);
                              const sku = item.external_data || '';
                              const uberPriceCents = item.price_info?.price || 0;
                              // Reverse-calculate app price from Uber price: cents / 100 * divisor
                              const appPriceRands = (uberPriceCents / 100) * markupDivisor;
                              const hasImage = !!item.image_url;
                              const isAlcoholic = item.dish_info?.classifications?.alcoholic_items === 1;
                              const stockQty = item._stockQty ?? null;
                              const isCustomPrice = !!item._isCustomPrice;
                              const isSuspended = !!item.suspension_info?.suspension?.suspend_until;

                              return (
                                <tr
                                  key={item.id}
                                  onClick={() => hasImage && setImagePreview({ url: item.image_url, name, sku })}
                                  style={{ cursor: hasImage ? 'pointer' : 'default' }}
                                >
                                  <td>
                                    {hasImage ? (
                                      <Tooltip title="Click to view image">
                                        <ImageIcon sx={{ fontSize: 16, color: '#43a047' }} />
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title="No image">
                                        <NoImageIcon sx={{ fontSize: 16, color: '#bdbdbd' }} />
                                      </Tooltip>
                                    )}
                                  </td>
                                  <td>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {name}
                                      {isAlcoholic && (
                                        <LiquorIcon sx={{ fontSize: 14, color: '#e65100' }} />
                                      )}
                                      {isSuspended && (
                                        <Chip label="suspended" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#ffcdd2', color: '#c62828' }} />
                                      )}
                                    </Box>
                                  </td>
                                  <td>
                                    <Chip
                                      label={sku}
                                      size="small"
                                      sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.7rem',
                                        height: 22,
                                      }}
                                    />
                                  </td>
                                  <td>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 700,
                                        fontFamily: 'monospace',
                                        fontSize: '0.8rem',
                                        color: stockQty === null ? '#bdbdbd' : stockQty <= 0 ? '#c62828' : stockQty <= 5 ? '#e65100' : GREEN,
                                      }}
                                    >
                                      {stockQty ?? '—'}
                                    </Typography>
                                  </td>
                                  <td>{formatCurrency(Math.round(appPriceRands * 100))}</td>
                                  <td>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 700,
                                          color: uberPriceCents > 0 ? GREEN : 'error.main',
                                          fontSize: '0.8rem',
                                        }}
                                      >
                                        {formatCurrency(uberPriceCents)}
                                      </Typography>
                                      {isCustomPrice && (
                                        <Chip label="custom" size="small" sx={{ fontSize: '0.55rem', height: 16, bgcolor: '#e3f2fd', color: '#1565c0' }} />
                                      )}
                                    </Box>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          );
        })}

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setStep(0)}
            startIcon={<BackIcon />}
            sx={{ borderColor: GREEN, color: GREEN }}
          >
            Rebuild
          </Button>
          <Button
            variant="contained"
            onClick={() => setConfirmOpen(true)}
            disabled={pushing || items.length === 0}
            startIcon={pushing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
            sx={{
              bgcolor: GREEN,
              '&:hover': { bgcolor: GREEN_DARK },
              px: 4,
              fontWeight: 700,
              textTransform: 'none',
            }}
          >
            {pushing ? 'Pushing...' : `Push ${items.length} Items to Uber`}
          </Button>
        </Box>

        {/* Confirmation dialog */}
        <Dialog
          open={confirmOpen}
          onClose={() => !pushing && setConfirmOpen(false)}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Confirm Full Menu Replacement</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              This will <strong>replace the entire Uber Eats menu</strong> with{' '}
              <strong>{items.length} items</strong> in{' '}
              <strong>{categories.length} categories</strong>
              {dc.added > 0 ? <>, plus <strong>{dc.added} new product{dc.added === 1 ? '' : 's'}</strong></> : null}.
            </Typography>
            {(previewData.removed?.length ?? 0) > 0 && (
              <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
                {previewData.removed!.length} item{previewData.removed!.length === 1 ? '' : 's'} on Uber now will come off (see Leaving Uber).
              </Alert>
            )}
            {(dc.declined + dc.fields) > 0 && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Also saved with the push: {dc.declined} declined new product{dc.declined === 1 ? '' : 's'} and {dc.fields} Lightspeed change{dc.fields === 1 ? '' : 's'} settled.
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The current menu will be backed up to S3 first. You can restore it
              from the Menu Backup &amp; Restore tool if needed.
            </Typography>
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              This will immediately affect the live Uber Eats menu visible to customers.
              Please verify the preview carefully before confirming.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 1 }}>
            <Button
              onClick={() => setConfirmOpen(false)}
              disabled={pushing}
              sx={{ color: 'text.secondary' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => handlePush()}
              disabled={pushing}
              startIcon={
                pushing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />
              }
              sx={{ bgcolor: GREEN, '&:hover': { bgcolor: GREEN_DARK } }}
            >
              {pushing ? 'Pushing...' : 'Confirm & Push'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  // Render — Step 3: Push Result
  // ---------------------------------------------------------------------------

  const renderStep3 = () => {
    if (!pushResult) return null;

    return (
      <Box>
        <Card
          variant="outlined"
          sx={{
            mb: 3,
            borderRadius: 3,
            borderColor: '#43a047',
            background: 'linear-gradient(135deg, #e8f5e9 0%, #fff 100%)',
          }}
        >
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CheckIcon sx={{ fontSize: 64, color: '#43a047', mb: 1.5 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#2e7d32' }}>
              Menu Pushed Successfully
            </Typography>
            <Typography variant="body1" sx={{ mb: 2.5, color: '#666' }}>
              The Uber Eats menu has been replaced. Uber shows it within a few minutes.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
              <Chip
                label={`${pushResult.itemCount} items`}
                sx={{ bgcolor: GREEN_LIGHT, color: GREEN, fontWeight: 700 }}
              />
              <Chip
                label={`${pushResult.categoryCount} categories`}
                sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700 }}
              />
              <Chip
                label={`${pushResult.menuCount} menus`}
                sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a', fontWeight: 700 }}
              />
            </Box>

            <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 2, p: 2, maxWidth: 500, mx: 'auto', mb: 3 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Backup Key
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}
              >
                {pushResult.backupKey}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={() => {
                  setStep(0);
                  setPreviewData(null);
                  setPushResult(null);
                  setDecisions(emptyDecisions());
                  setError(null);
                }}
                startIcon={<SyncIcon />}
                sx={{ bgcolor: GREEN, '&:hover': { bgcolor: GREEN_DARK } }}
              >
                Sync Again
              </Button>
              <Button
                variant="outlined"
                onClick={onBack}
                sx={{ borderColor: GREEN, color: GREEN }}
              >
                Back to Tools
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ p: 3, maxWidth: 1000 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <IconButton onClick={onBack} disabled={building || pushing}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Push Uber Menu
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 6 }}>
        Build the Uber Eats menu, settle new and mismatching products, preview it, and push it as a full menu replacement.
      </Typography>

      {/* Stepper */}
      <WizardStepper activeStep={step} />

      {/* Error banner */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* Step content */}
      {step === 0 && renderStep1()}
      {step === 1 && renderStep2()}
      {step === 2 && renderStep3()}

      {/* Image Preview Dialog */}
      <Dialog
        open={!!imagePreview}
        onClose={() => setImagePreview(null)}
        maxWidth="md"
      >
        {imagePreview && (
          <>
            <DialogTitle sx={{ pb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600}>{imagePreview.name}</Typography>
              <Typography variant="caption" color="text.secondary">{imagePreview.sku}</Typography>
            </DialogTitle>
            <DialogContent sx={{ textAlign: 'center', p: 2 }}>
              <Box
                component="img"
                src={imagePreview.url}
                alt={imagePreview.name}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  borderRadius: 2,
                  objectFit: 'contain',
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setImagePreview(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Price editing moved to Product Management page */}
    </Box>
  );
};

export default SyncAppToUber;
